import type { Api } from "../../services/api";

/**
 * What the Practice Profiles screen has checked, and whether it can be submitted.
 * Shared by the onboarding slide and the settings editor so the two cannot drift.
 *
 * Two states must stay distinct — "untouched" (whatever the screen opened with)
 * and "explicitly empty" (the catalogue does not describe me). That is why the
 * selection is nullable rather than seeded into a set on load.
 */
export type PracticeProfileOffer =
  Api["get"]["/webapp/public/practice-profiles/offered"]["output"]["body"];

export type OfferedProfile = PracticeProfileOffer["docs"][number];

/**
 * What evidence about this practitioner points at, or null — none found, lookup
 * failed, or not landed yet. It NEVER blocks: the screen renders on the
 * unchecked catalogue and this checks rows if it arrives — and it is the only
 * thing that ever does.
 */
export type PracticePreSelection =
  Api["post"]["/webapp/public/practice-profiles/pre-selection"]["output"]["body"];

/**
 * What a held answer says about the Specialty currently on screen.
 *
 * An answer is an answer to ONE catalogue's question, and the Specialty it was
 * given about travels with it. A practitioner who goes back and re-declares is
 * looking at a different catalogue — with a different axis and, thanks to the
 * many-to-many `specialtyIds`, possibly some of the same profiles — so the
 * previous answer is not a quieter version of an answer here. It is no answer,
 * and reading it as one showed the first Specialty's practices checked under the
 * second and wrote them at finalize.
 */
export function practiceAnswerForSpecialty(args: {
  readonly held: {
    readonly specialtyUid: string;
    readonly profileUids: readonly string[] | null;
    readonly disambiguation: readonly string[] | null;
    readonly declaration: string | null;
  } | null;
  readonly specialtyUid: string;
}): {
  readonly profileUids: readonly string[] | null;
  readonly disambiguation: readonly string[] | null;
  readonly declaration: string | null;
} {
  if (args.held === null || args.held.specialtyUid !== args.specialtyUid)
    return { profileUids: null, disambiguation: null, declaration: null };
  return {
    profileUids: args.held.profileUids,
    disambiguation: args.held.disambiguation,
    declaration: args.held.declaration,
  };
}

/**
 * The checked set. `explicit` is what the practitioner has touched — null until
 * they do.
 *
 * Untouched, the ONLY thing that checks a row is `evidence` — what the lookup
 * actually found about THIS practitioner. Nothing about the catalogue checks
 * anything: the offer describes a Specialty, not a person, and a row checked
 * because the Specialty contains it is the screen putting words in their mouth
 * on a question ("does this describe you") whose whole value is that they
 * answered it. So no evidence, or evidence that found nothing, is an empty
 * screen — which is now a screen that cannot be SAVED in the arm
 * (`canSubmitPracticeProfiles`). That reversed: an empty save used to be a real
 * answer. It stopped being one because the catalogue is expert-reviewed, so
 * "none of these describe me" reports a hole in the catalogue rather than a
 * preference, and recording it as a preference made the hole unreadable.
 *
 * The axis answer FILTERS the list (`practicesOnAxisSide`); it checks nothing,
 * which is why it is not a parameter here.
 */
export function selectedProfileUids(args: {
  readonly explicit: readonly string[] | null;
  /** Only the uids are read. Narrowed to that so a caller holding a PERSISTED
   * reading (the campaign) does not have to invent a `source` it has no opinion
   * about — the full signup body still satisfies it. */
  readonly evidence: Pick<PracticePreSelection, "profileUids"> | null;
}): ReadonlySet<string> {
  if (args.explicit !== null) return new Set(args.explicit);
  if (args.evidence === null) return new Set();
  return new Set(args.evidence.profileUids);
}

/**
 * Whether the signup slide shows the RECAP — the practices the agent recognised,
 * named and read back — instead of the catalogue.
 *
 * It exists because the list was answered by bulk. A screen that asks a
 * practitioner to arbitrate nineteen rows gets "check all" from the one in a
 * hurry and, for a different reason, from the one who reads every row and cannot
 * separate "I practise this" from "this interests me". Both produce a profile
 * that describes nobody. The recap asks a smaller question — is this right —
 * against four names the agent derived from their own professional record.
 *
 * Whether there IS one to show, not whether it is on screen: the host decides
 * that, because only the host knows the practitioner has since opened the list
 * ("Edit", or the axis question). Splitting it that way is what lets Back return
 * to the recap — the same answer, asked again after the stored screen is
 * cleared, rather than a second rule about how one got here.
 *
 * Two conditions, and each rules out a state where reading the evidence back
 * would be a lie:
 *
 * - `explicit`: they have touched a row. The recap names the AGENT's answer, and
 *   once their own exists it is the one that counts — which is also why Back
 *   stops offering the recap after the first tap.
 * - evidence naming at least one practice. Nothing else ever checks a row
 *   (`selectedProfileUids`), so with no evidence there is nothing to recap and
 *   the catalogue is the only honest screen.
 *
 * Deliberately says nothing about the axis: an agent that named practices but no
 * side still gets a recap, and the axis stays unanswered. The practices are what
 * the reader recognises themselves in, and holding the recap back over a
 * question the recap does not ask would send them to the list for nothing.
 */
export function showsPracticeRecap(args: {
  readonly evidence: Pick<PracticePreSelection, "profileUids"> | null;
  readonly explicit: readonly string[] | null;
}): boolean {
  if (args.explicit !== null) return false;
  if (args.evidence === null) return false;
  return args.evidence.profileUids.length > 0;
}

/**
 * The practices the list shows once the axis is answered: the UNION of the
 * answered sides, plus everything ORTHOGONAL to the axis (a profile carrying no
 * value on it is not what the question was about — a surgeon answering "adult"
 * still practises their oncological speciality). `offAxis` IS that
 * orthogonality: the offer sets it exactly on the profiles carrying no value on
 * the axis (`practiceProfileOffer`).
 *
 * A union, because the axis cuts the CATALOGUE and not the practitioner: one
 * practice carries one species, one working week carries as many as it carries.
 * A vet doing poultry and swine reads both catalogues here, and only the five
 * remaining species are out of the way.
 *
 * This is the axis's whole effect: it narrows 30 rows to the ones worth reading,
 * and checks none of them. Checking a whole side would answer "does this
 * describe you" on the practitioner's behalf, for fifteen practices at once.
 *
 * Consumed by the picker itself (`PracticeProfilesPicker`), which is the only
 * place a list of practices is rendered — so every host narrows identically, and
 * none of them has to know the axis exists.
 */
export function practicesOnAxisSide(args: {
  readonly offer: PracticeProfileOffer;
  readonly disambiguation: readonly string[] | null;
}): readonly OfferedProfile[] {
  const axis = args.offer.disambiguation;
  const answered = args.disambiguation;
  // Nothing answered lists everything — and so does an answer emptied by
  // unchecking its last side, which is a question back at its start rather than
  // a catalogue with nothing in it.
  if (axis === null || answered === null || answered.length === 0)
    return args.offer.docs;
  const chosen = new Set(
    axis.options
      .filter((option) => answered.includes(option.value))
      .flatMap((option) => option.profileUids)
  );
  return args.offer.docs.filter(
    (profile) => profile.offAxis || chosen.has(profile.uid)
  );
}

/**
 * The answer to the axis question: the practitioner's own, otherwise the sides the
 * pre-selection settled — their official qualification, or the sides the practices
 * it recognised sit on. What never reaches here is a bare CLAIM about the axis off
 * the open web; the endpoint drops that before answering (`readInferredProfiles`),
 * because being wrong on this axis is not the one tap it is elsewhere.
 *
 * Derived in one place because four things must agree about it: what is checked,
 * which options render selected, whether Continue is enabled, and which of the
 * screen's two questions opens — an answer already in hand means the question is
 * skipped, not asked and pre-filled.
 *
 * An evidence answer of NO sides is no answer: `null`, not `[]`, so the question
 * still opens.
 */
export function effectivePracticeDisambiguation(args: {
  readonly answer: readonly string[] | null;
  /** Only the sides are read — same narrowing as `selectedProfileUids`. */
  readonly evidence: Pick<PracticePreSelection, "disambiguationValues"> | null;
}): readonly string[] | null {
  if (args.answer !== null) return args.answer;
  if (args.evidence === null) return null;
  return args.evidence.disambiguationValues.length === 0
    ? null
    : args.evidence.disambiguationValues;
}

/**
 * Which of the two questions the Practice Profiles screen is on.
 *
 * The catalogue is what forces this apart. A Specialty is cut along a disjoint
 * axis — species for Animal Health (7 values), patient population for Surgery
 * (3) — and until it is answered, the list is every side of it at once. Animal
 * Health has all 30 of its profiles on that axis, so before the axis is answered
 * there is nothing to read: 30 rows spanning seven species is not a description
 * of anybody.
 */
export type PracticePickerPhase = "axis" | "profiles";

/**
 * Which question is up — decided by the OFFER first, and that is the whole
 * reason this is a function and not a field each host computes.
 *
 * A Specialty with no disjoint axis has no first question, so `"axis"` is not a
 * phase its screen can be in. That state used to be representable, and every
 * reader defended itself against it separately (`offer.disambiguation === null ||
 * …`, in the picker, the editor's CTA, its label, its search field, and the
 * carousel's footer band). The one reader that forgot swallowed the practitioner's
 * first Continue: the press advanced the phase out of a question that had never
 * been on screen, and the slide only left on the second. One resolution here,
 * instead of six defences and a seventh place to forget.
 *
 * `stored` is what the host holds — the carousel in its flow store, the editor in
 * its form — and it is null until the practitioner moves between the two
 * questions themselves. `arrivedAnswered` opens straight on the list for someone
 * whose answer is already in hand (evidence at signup, their saved answer in
 * settings): an answer already given is a question skipped, not one asked and
 * pre-filled.
 *
 * No offer is no question either. The carousel reads this while the catalogue is
 * still in flight, and its Continue is disabled throughout that wait
 * (`isCurrentStepValid`), so nothing acts on the value until there is an offer.
 */
export function resolvePracticePhase(args: {
  readonly offer: PracticeProfileOffer | null;
  readonly stored: PracticePickerPhase | null;
  readonly arrivedAnswered: boolean;
}): PracticePickerPhase {
  if (args.offer === null || args.offer.disambiguation === null)
    return "profiles";
  if (args.stored !== null) return args.stored;
  return args.arrivedAnswered ? "profiles" : "axis";
}

/**
 * Whether the screen may be left. The ONLY blocking point of the flow, and the
 * one place both surfaces read — a second mechanism beside it would let signup
 * and settings answer the same question differently.
 *
 * Two things are required. An unanswered disjoint question cannot be guessed
 * past: at least one side, since a practitioner may name several and naming none
 * is the question still open rather than a shorter answer to it. And at least
 * one ANSWER about the practice — a practice checked, or the practitioner's own
 * words. Zero of both is a screen nobody answered.
 *
 * The words count because the catalogue is expert-reviewed and INCOMPLETE:
 * "none of these describe me" reports a hole in it, and the honest way to record
 * a hole is with what should have filled it. Requiring a checked row from
 * somebody whose practice is not listed only ever bought a practice they do not
 * perform.
 *
 * This is an INVARIANT, not a signup rule: nothing may bring an in-arm account
 * to zero answers, which is what makes zero mean "never asked" again. It is why
 * both fields are required here rather than defaulted — every caller has to say
 * what it holds.
 *
 * Blocking means the SAVE is refused. The carousel's back and the settings
 * chevron still leave the screen; the Explore gate, while owed, has no other exit.
 */
export function canSubmitPracticeProfiles(args: {
  readonly offer: PracticeProfileOffer;
  readonly disambiguation: readonly string[] | null;
  readonly selected: ReadonlySet<string>;
  readonly declaration: string | null;
}): boolean {
  if (args.selected.size === 0 && args.declaration === null) return false;
  return canLeavePracticeAxisQuestion(args);
}

/**
 * Whether the FIRST of the screen's two questions may be left: at least one side
 * named, or a Specialty that asks nothing.
 *
 * That last clause is NOT dead code for an offer with no axis, however much it
 * looks it from the signup footer — `resolvePracticePhase` never yields `"axis"`
 * there, so that caller cannot reach it. `canSubmitPracticeProfiles` can: it
 * delegates the axis half of its own rule here, and for a Specialty that asks
 * nothing that half is satisfied. Turning this into an invariant throw crashes
 * every axis-less save (Dermatology first).
 *
 * Split out of the rule above rather than folded into it, because the practice
 * requirement must NOT gate this button. On the axis question the footer is the
 * way out of the axis question, and the list of practices that would satisfy the
 * requirement is behind it — gating it with the requirement would seal a
 * practitioner inside the first question with no way to answer the second.
 *
 * Only the signup surface reads this. Settings never blocks the axis question:
 * an existing account is not held to a question it never agreed to answer, and
 * walking past it there lists the whole catalogue.
 */
export function canLeavePracticeAxisQuestion(args: {
  readonly offer: PracticeProfileOffer;
  readonly disambiguation: readonly string[] | null;
}): boolean {
  if (args.offer.disambiguation === null) return true;
  return args.disambiguation !== null && args.disambiguation.length > 0;
}

/**
 * Add or remove one side of the axis, returning BOTH halves of the new answer —
 * the sides, and the practices that survive them. They move together because
 * they cannot disagree: a practice whose only side has just been dropped is one
 * the list is about to stop showing, and leaving it checked would save a practice
 * the practitioner can no longer see, let alone uncheck.
 *
 * ADDING a side checks nothing: it widens what the list SHOWS
 * (`practicesOnAxisSide`), and naming fifteen practices is not performing them.
 * REMOVING one drops exactly the picks that lived on it and nothing else —
 * everything orthogonal to the axis, and everything on the sides still answered,
 * is the practitioner's own and stays.
 *
 * Returned as an explicit selection rather than by clearing one: on the settings
 * surface the seed is the SAVED set, so clearing would re-seed from it.
 */
export function toggleDisambiguation(args: {
  readonly offer: PracticeProfileOffer;
  readonly selected: ReadonlySet<string>;
  readonly current: readonly string[] | null;
  readonly value: string;
}): {
  readonly disambiguation: string[];
  readonly profileUids: string[];
} {
  const { disambiguation } = args.offer;
  if (disambiguation === null)
    throw new Error(
      "should not happen: unreachable state — answered a question the offer does not ask"
    );
  const held = args.current === null ? [] : args.current;
  const next = held.includes(args.value)
    ? held.filter((value) => value !== args.value)
    : [...held, args.value];

  const kept = new Set(
    disambiguation.options
      .filter((option) => next.includes(option.value))
      .flatMap((option) => option.profileUids)
  );
  const onAxis = new Set(
    disambiguation.options.flatMap((option) => option.profileUids)
  );

  return {
    disambiguation: next,
    profileUids: [
      ...new Set(
        [...args.selected].filter((uid) => !onAxis.has(uid) || kept.has(uid))
      ),
    ],
  };
}

/**
 * Toggle one profile, returning the whole new explicit selection. The first tap
 * on any row turns what the screen opened with into the practitioner's own, so it
 * starts from what was on screen — otherwise unchecking one row would drop every
 * other row the evidence had checked.
 *
 * No size limit, in either direction. A practitioner describing their working
 * life is not filling in a query parameter, and nothing downstream truncates the
 * answer: the feed reads one practice at a time, so a wide answer costs chips and
 * nothing else.
 */
export function togglePracticeProfile(
  selected: ReadonlySet<string>,
  uid: string
): string[] {
  const next = new Set(selected);
  if (next.has(uid)) next.delete(uid);
  else next.add(uid);
  return [...next];
}

/**
 * The order the picker lists practices in: what was checked WHEN THE LIST OPENED,
 * first.
 *
 * The list is long enough for a catalogue to push a practitioner's own answer
 * below the fold, where "here is what we think you do, correct it" becomes an
 * unreadable instruction. Reading their answer back to them is the screen's whole
 * job, so it goes where it can be read.
 *
 * `checkedOnOpen` is the answer the screen ARRIVED with — the evidence lookup, or
 * the saved answer — and deliberately NOT the live selection. Ordering by the live one would move a row out from under the finger
 * that just toggled it, and worse, would make a mis-tap self-confirming: the row
 * you meant to uncheck slides away and a different one takes its place.
 *
 * So the order is stable for as long as the list is open, and it is recomputed
 * only when the baseline itself changes — the evidence lookup landing, or a save
 * establishing a new saved answer. Both are moments when the list genuinely says
 * something new.
 *
 * Within each of the two groups the incoming order is PRESERVED, so this composes
 * with whatever the caller already ordered by rather than replacing it — the
 * picker hands it an alphabetical list and gets two alphabetical groups back.
 */
export function practiceProfilesInPickerOrder(args: {
  readonly profiles: readonly OfferedProfile[];
  readonly checkedOnOpen: ReadonlySet<string>;
}): readonly OfferedProfile[] {
  return [
    ...args.profiles.filter((profile) => args.checkedOnOpen.has(profile.uid)),
    ...args.profiles.filter((profile) => !args.checkedOnOpen.has(profile.uid)),
  ];
}

/**
 * What the practitioner changed about the answer the screen arrived with.
 *
 * Both hosts of the picker report it, and both hold the same two sets already —
 * the baseline the list is ordered by (`practiceProfilesInPickerOrder`) and the
 * live selection — so the arithmetic lives here rather than twice at the two
 * call sites, where the two copies would be free to disagree about what "added"
 * means.
 *
 * GROSS, on purpose. The net alone reports "unticked two of the agent's, added
 * three" as a light correction of +1, which is the reading that makes an agent
 * with a systematic bias look accurate. The net stays derivable (`added -
 * removed`); these two are not derivable from it.
 *
 * `viaSearch` and `viaAssist` narrow the additions to the ones reached through
 * the search field. They are intersected with the additions rather than trusted:
 * a row ticked from a search result and then unticked is not an addition, and a
 * provenance set that outlived the selection it describes would report it as one.
 */
export function practiceSelectionDelta(args: {
  readonly checkedOnOpen: ReadonlySet<string>;
  readonly selected: ReadonlySet<string>;
  readonly viaSearch: ReadonlySet<string>;
  readonly viaAssist: ReadonlySet<string>;
}): {
  readonly added: number;
  readonly removed: number;
  readonly addedViaSearch: number;
  readonly addedViaAssist: number;
} {
  const added = [...args.selected].filter(
    (uid) => !args.checkedOnOpen.has(uid)
  );
  const removed = [...args.checkedOnOpen].filter(
    (uid) => !args.selected.has(uid)
  );
  return {
    added: added.length,
    removed: removed.length,
    addedViaSearch: added.filter((uid) => args.viaSearch.has(uid)).length,
    addedViaAssist: added.filter((uid) => args.viaAssist.has(uid)).length,
  };
}

/**
 * How many practices the agent named that THIS catalogue carries.
 *
 * The one definition of `recognised_practice_count`, for both surfaces, because the two
 * were computing it differently and the difference was invisible in the data.
 * Signup reads `POST /pre-selection`, which returns `inferPracticeProfiles` raw.
 * The campaign seed goes through `invitableProfileUids`, which intersects with
 * the offer AND caps at `ALL_PRACTICES_MAX_ARMS` (6). So an agent that named nine
 * practices reported nine on one flow and six on the other — and the mean, the
 * distribution and the share recognised at all are exactly the three numbers the
 * event exists to make comparable across `flow`.
 *
 * The intersection stays, the cap does not. A practice the catalogue no longer
 * carries was not recognised in any sense the reader could see, so counting it
 * would credit the agent for a row that cannot appear. The cap is a rule about
 * how many rows a screen or an email SHOWS; it says nothing about what the agent
 * found, and folding it in here would make a display decision look like a
 * property of the inference.
 *
 * What checked the screen is a different question, and the deltas answer it:
 * they count against the seed, and `pre_selection_source` names it.
 */
export function inferredPracticeCount(args: {
  readonly evidence: Pick<PracticePreSelection, "profileUids"> | null;
  readonly offer: PracticeProfileOffer | null;
}): number {
  if (args.evidence === null || args.offer === null) return 0;
  const offered = new Set(args.offer.docs.map((profile) => profile.uid));
  return [...new Set(args.evidence.profileUids)].filter((uid) =>
    offered.has(uid)
  ).length;
}

/**
 * Which practices were reached THROUGH the search field, after a change to the
 * selection.
 *
 * One rule for both hosts. It was written twice — once in the signup flow, once
 * in the settings editor — and the two copies already disagreed: signup left a
 * stale `viaAssist` entry standing when a row was re-ticked off a string match,
 * inflating the one number that says whether the assisted layer earns its
 * request.
 *
 * Every change routes through here, the bulk buttons included. That is the other
 * half of the defect: "Uncheck all" then "Check all" left a provenance set
 * describing rows the field never won, and because those rows were genuine
 * additions again, no intersection downstream could tell. Provenance belongs to
 * the GESTURE that made a row, so a row remade by another gesture loses it.
 *
 * `reached` is the row this change ticked through the field, or null for a
 * change that is not a field tick: a bulk set, an axis answer, an untick. The
 * filter against `selected` does the forgetting on its own, which is why an
 * untick needs no case of its own.
 */
export function practiceSearchProvenanceAfter(args: {
  readonly current: {
    readonly viaSearch: ReadonlySet<string>;
    readonly viaAssist: ReadonlySet<string>;
  };
  /** The selection AFTER the change, never before: a set that outlives the rows
   * it describes is what made the bulk buttons mis-report. */
  readonly selected: ReadonlySet<string>;
  readonly reached: {
    readonly uid: string;
    /** The field held a query when the row was ticked. */
    readonly viaSearch: boolean;
    /** …and the row was one only the assisted layer surfaced. */
    readonly viaAssist: boolean;
  } | null;
}): {
  readonly viaSearch: ReadonlySet<string>;
  readonly viaAssist: ReadonlySet<string>;
} {
  const keep = (held: ReadonlySet<string>): Set<string> =>
    new Set([...held].filter((uid) => args.selected.has(uid)));
  const viaSearch = keep(args.current.viaSearch);
  const viaAssist = keep(args.current.viaAssist);
  const reached = args.reached;
  // A row the change did not tick through the field keeps nothing: `reached` is
  // null, or it names a row the change removed.
  if (reached === null || !args.selected.has(reached.uid))
    return { viaSearch, viaAssist };
  if (reached.viaSearch) viaSearch.add(reached.uid);
  else viaSearch.delete(reached.uid);
  if (reached.viaSearch && reached.viaAssist) viaAssist.add(reached.uid);
  else viaAssist.delete(reached.uid);
  return { viaSearch, viaAssist };
}
